import * as React from 'react';

import { cn } from '../utils/cn';
import {
  formControlFocusTransition,
  formControlSurfaceClass,
} from '../utils/form-control-styles';

export type InputProps = React.ComponentProps<'input'> & {
  /**
   * When true (default), Enter blurs the field — confirms edits (e.g. blur
   * autosave) and dismisses the mobile keyboard. Never apply this to
   * multiline fields; use `Textarea` for notes. Set `false` when Enter should
   * keep focus (e.g. tag entry that accepts multiple values).
   */
  confirmOnEnter?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      confirmOnEnter = true,
      enterKeyHint,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const resolvedEnterKeyHint =
      enterKeyHint ??
      (confirmOnEnter ? (type === 'search' ? 'search' : 'done') : undefined);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);

      if (!confirmOnEnter) return;
      if (event.key !== 'Enter') return;
      // IME composition (e.g. CJK): Enter confirms a candidate, not the field.
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      // Custom handlers (tags, etc.) that call preventDefault keep focus.
      if (event.defaultPrevented) return;

      const input = event.currentTarget;
      const dismiss = () => {
        if (!input.isConnected) return;
        // Submit validation flagged this field and kept it focused: stay.
        if (input.getAttribute('aria-invalid') === 'true') return;
        input.blur();
      };

      // Inside a form, blur only after native implicit submit has run: it
      // fires after keydown, so a microtask blur (runs right after this
      // handler) moved focus away first and Enter never submitted.
      if (input.form) {
        setTimeout(dismiss, 0);
      } else {
        dismiss();
      }
    };

    // Focus ring = 2px ring + 2px offset painted in the canvas colour. A
    // consumer that draws its own frame must drop both
    // (`focus-visible:ring-0 focus-visible:ring-offset-0`): `ring-0` alone
    // keeps a canvas-coloured halo that covers the wrapper's border.
    return (
      <input
        type={type}
        className={cn(
          'flex h-10 w-full rounded-md border px-3 py-2 text-base text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:aria-invalid:border-destructive focus-visible:aria-invalid:ring-destructive/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm aria-invalid:border-destructive',
          formControlSurfaceClass,
          formControlFocusTransition,
          className,
        )}
        ref={ref}
        {...props}
        enterKeyHint={resolvedEnterKeyHint}
        onKeyDown={handleKeyDown}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
